import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * TaskMatrix — the TODO Funnel's checklist table (Phase 5).
 * docs/TODO-FUNNEL-VISION.md §3.4: "DATE | TASK | ASSIGNEE | DETAIL with
 * column filters and check marks" — the presentation/email/bulk-work
 * complement to the bubble canvas. Same data, same callbacks.
 *
 * - Sortable columns (click headers), filter row (search / list / account).
 * - Check circle completes (strike, then the parent's refetch removes).
 * - Inline edits: title, due date, assignee (with people datalist),
 *   list, priority — all through onUpdate, changed field only.
 * - Exports: "Copy for email" (rich HTML table on the clipboard — paste
 *   straight into Gmail), CSV download, and Slides when the host provides
 *   onExportSlides (GAS builds the deck in-tenant).
 *
 * Presentational + MUI-free; shared by TekStak and GAS.
 */
import React, { useMemo, useState } from 'react';
import { Check, Copy, Download, Presentation, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, Undo2, MessageSquare, Sun } from 'lucide-react';
import { taskScore, priorityBand, snapScore, useUndoableTasks, useOptimisticShelf, tasksToSlackText, exportEvents, TaskBulkBar } from '../TaskBubbleCanvas/TaskBubbleCanvas.js';
const LISTS = ['inbox', 'next_actions', 'waiting_for', 'projects', 'someday_maybe', 'reference'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const PRIO_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
function fmtDate(d) {
    if (!d)
        return '';
    const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m)
        return '';
    return `${m[2]}/${m[3]}/${m[1].slice(2)}`;
}
function isoOf(d) {
    return d ? String(d).slice(0, 10) : '';
}
export function TaskMatrix({ tasks: tasksProp, people, onComplete, onReopen, onUpdate, onArchive, onBulkArchive, onDelete, onAssign, onExportSlides, onLogEvents, exportTitle = 'Tasks' }) {
    const [sortKey, setSortKey] = useState('due');
    const [sortDir, setSortDir] = useState(1);
    const [q, setQ] = useState('');
    const [listFilter, setListFilter] = useState('');
    const [acctFilter, setAcctFilter] = useState('');
    const [assigneeFilter, setAssigneeFilter] = useState('');
    const [prioFilter, setPrioFilter] = useState('');
    const [headMenu, setHeadMenu] = useState(null);
    const [struck, setStruck] = useState({});
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState('');
    const [notice, setNotice] = useState('');
    const [selected, setSelected] = useState({});
    const selectedIds = Object.keys(selected).filter((k) => selected[k]);
    const clearSelection = () => setSelected({});
    React.useEffect(() => {
        if (!selectedIds.length)
            return;
        const onKey = (e) => { if (e.key === 'Escape')
            clearSelection(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedIds.length]);
    /** Copy/export target: the selection when present, else every row. */
    const exportRows = () => (selectedIds.length ? rows.filter((t) => selected[t.id]) : rows);
    // `tasks` from here on carries the optimistic field overlay — edits paint
    // in the table instantly (UI-responsiveness review 2026-08-08).
    const { tasks, update: undoableUpdate, complete: undoableComplete, undo, canUndo } = useUndoableTasks(tasksProp, onUpdate, onComplete, onReopen);
    // Archived rows strike out of the table the instant Archive is clicked
    // (shared optimistic mechanism — request behind, revert on reject).
    const shelf = useOptimisticShelf(tasks, { onArchive, onBulkArchive });
    const accountsInData = useMemo(() => [...new Set(tasks.map((t) => t.account_label).filter(Boolean))].sort(), [tasks]);
    const rows = useMemo(() => {
        const query = q.trim().toLowerCase();
        const filtered = tasks
            .filter((t) => !t.status || t.status !== 'cancelled')
            .filter((t) => !(t.id in shelf.hidden))
            .filter((t) => !listFilter || t.gtd_list === listFilter)
            .filter((t) => !acctFilter || t.account_label === acctFilter)
            .filter((t) => !assigneeFilter || (assigneeFilter === '<none>' ? !t.waiting_for_who : t.waiting_for_who === assigneeFilter))
            .filter((t) => !prioFilter || priorityBand(taskScore(t)) === prioFilter)
            .filter((t) => !query ||
            t.title.toLowerCase().includes(query) ||
            (t.waiting_for_who || '').toLowerCase().includes(query) ||
            (t.account_label || '').toLowerCase().includes(query) ||
            (t.tags || []).some((tag) => tag.toLowerCase().includes(query)));
        const val = (t) => {
            switch (sortKey) {
                case 'due': return t.due_date ? String(t.due_date).slice(0, 10) : '9999';
                case 'title': return t.title.toLowerCase();
                case 'account': return (t.account_label || '~').toLowerCase();
                case 'assignee': return (t.waiting_for_who || '~').toLowerCase();
                case 'priority': return PRIO_RANK[t.priority || 'medium'];
                case 'list': return LISTS.indexOf(t.gtd_list);
            }
        };
        return filtered.sort((a, b) => {
            const av = val(a);
            const bv = val(b);
            return (av < bv ? -1 : av > bv ? 1 : 0) * sortDir;
        });
    }, [tasks, q, listFilter, acctFilter, assigneeFilter, prioFilter, sortKey, sortDir, shelf.hidden]);
    const sortBy = (key) => {
        if (sortKey === key)
            setSortDir((d) => (d === 1 ? -1 : 1));
        else {
            setSortKey(key);
            setSortDir(1);
        }
    };
    const startEdit = (id, field, seed) => {
        if (!onUpdate)
            return;
        setEditing({ id, field });
        setDraft(seed);
    };
    const commitEdit = () => {
        if (!editing || !onUpdate)
            return void setEditing(null);
        const v = draft.trim();
        const t = tasks.find((x) => x.id === editing.id);
        if (t) {
            if (editing.field === 'title' && v && v !== t.title)
                undoableUpdate(t.id, { title: v });
            if (editing.field === 'due' && v !== isoOf(t.due_date))
                undoableUpdate(t.id, { due_date: v || null });
            if (editing.field === 'assignee' && v !== (t.waiting_for_who || '')) {
                undoableUpdate(t.id, { waiting_for_who: v || null, ...(v ? { gtd_list: 'waiting_for' } : {}) });
            }
        }
        setEditing(null);
    };
    const check = (id) => {
        setStruck((s) => ({ ...s, [id]: true }));
        window.setTimeout(() => undoableComplete?.(id), 350);
    };
    const flash = (msg) => {
        setNotice(msg);
        window.setTimeout(() => setNotice(''), 2500);
    };
    // ── Exports ─────────────────────────────────────────────────────────
    const exportCells = (t) => ({
        date: fmtDate(t.due_date),
        task: t.title,
        account: t.account_label || '',
        assignee: t.waiting_for_who || '',
        detail: [t.gtd_list.replace('_', ' '), t.priority !== 'medium' ? t.priority : '', t.description || '']
            .filter(Boolean)
            .join(' — '),
    });
    const copyForEmail = async () => {
        const target = exportRows();
        const cellStyle = 'padding:6px 10px;border:1px solid #d7dbe0;font-size:13px;';
        const html = `<div style="font-family:Arial,Helvetica,sans-serif">` +
            `<p style="font-size:14px;font-weight:bold;margin:0 0 8px">${exportTitle} — ${new Date().toLocaleDateString()}</p>` +
            `<table style="border-collapse:collapse">` +
            `<tr>${['', 'Date', 'Task', 'Account', 'Assignee', 'Detail']
                .map((h) => `<th style="${cellStyle}background:#f1f3f5;text-align:left">${h}</th>`)
                .join('')}</tr>` +
            target
                .map((t) => {
                const c = exportCells(t);
                return `<tr>${['&#9744;', c.date, c.task, c.account, c.assignee, c.detail]
                    .map((v) => `<td style="${cellStyle}">${v}</td>`)
                    .join('')}</tr>`;
            })
                .join('') +
            `</table></div>`;
        const text = target
            .map((t) => {
            const c = exportCells(t);
            return `[ ] ${c.date ? c.date + '  ' : ''}${c.task}${c.assignee ? ' — ' + c.assignee : ''}${c.account ? ' (' + c.account + ')' : ''}`;
        })
            .join('\n');
        try {
            if (navigator.clipboard && window.ClipboardItem) {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([text], { type: 'text/plain' }),
                    }),
                ]);
            }
            else {
                await navigator.clipboard.writeText(text);
            }
            flash(`Copied ${target.length} row(s) — paste into an email`);
            onLogEvents?.(exportEvents(target, 'exported_email', { target: 'clipboard' }));
            clearSelection();
        }
        catch (e) {
            flash('Copy failed — browser blocked clipboard access');
        }
    };
    const copyForSlack = async () => {
        const target = exportRows();
        try {
            await navigator.clipboard.writeText(tasksToSlackText(target));
            flash(`Copied ${target.length} row(s) — paste into Slack`);
            onLogEvents?.(exportEvents(target, 'exported_slack', { target: 'clipboard' }));
            clearSelection();
        }
        catch (e) {
            flash('Copy failed — browser blocked clipboard access');
        }
    };
    const downloadCsv = () => {
        const target = exportRows();
        const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
        const csv = [
            ['Date', 'Task', 'Account', 'Assignee', 'List', 'Priority', 'Tags'].map(esc).join(','),
            ...target.map((t) => [
                isoOf(t.due_date),
                t.title,
                t.account_label || '',
                t.waiting_for_who || '',
                t.gtd_list,
                t.priority || 'medium',
                (t.tags || []).join(' '),
            ]
                .map(esc)
                .join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `tasks-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        flash(`Exported ${target.length} row(s) to CSV`);
        onLogEvents?.(exportEvents(target, 'exported_csv', {}));
        clearSelection();
    };
    const assigneesInData = React.useMemo(() => Array.from(new Set(tasks.map((t) => t.waiting_for_who).filter(Boolean))).sort(), [tasks]);
    // Column-value filters: header TEXT opens the picker; the arrows sort.
    const headFilters = {
        account: {
            value: acctFilter,
            set: setAcctFilter,
            options: accountsInData.map((a) => ({ v: a, label: a })),
        },
        assignee: {
            value: assigneeFilter,
            set: setAssigneeFilter,
            options: [{ v: '<none>', label: 'Unassigned' }, ...assigneesInData.map((a) => ({ v: a, label: a }))],
        },
        list: {
            value: listFilter,
            set: setListFilter,
            options: LISTS.map((l) => ({ v: l, label: l.replace('_', ' ') })),
        },
        priority: {
            value: prioFilter,
            set: setPrioFilter,
            options: [
                { v: 'critical', label: 'Red — ASAP' },
                { v: 'elevated', label: 'Yellow — rising' },
                { v: 'neutral', label: 'Neutral' },
            ],
        },
    };
    const header = (label, key) => {
        const f = headFilters[key];
        const active = f?.value ? f.options.find((o) => o.v === f.value)?.label || f.value : null;
        return (_jsxs("th", { className: "tmx-th", children: [_jsxs("span", { className: "tmx-head", children: [_jsxs("button", { type: "button", className: `tmx-htext ${active ? 'tmx-htext-active' : ''}`, title: f ? `Filter by ${label.toLowerCase()}` : `Sort by ${label.toLowerCase()}`, onClick: () => (f ? setHeadMenu(headMenu === key ? null : key) : sortBy(key)), children: [active || label, f && _jsx(ChevronDown, { size: 9 })] }), _jsx("button", { type: "button", className: `tmx-harrow ${sortKey === key ? 'tmx-sort-active' : ''}`, title: `Sort by ${label.toLowerCase()} (${sortKey === key && sortDir === 1 ? 'descending next' : 'ascending next'})`, "aria-label": `Sort by ${label}`, onClick: () => sortBy(key), children: sortKey === key ? (sortDir === 1 ? _jsx(ArrowUp, { size: 10 }) : _jsx(ArrowDown, { size: 10 })) : _jsx(ArrowUpDown, { size: 10 }) })] }), headMenu === key && f && (_jsxs(_Fragment, { children: [_jsx("span", { className: "tmx-hveil", onMouseDown: () => setHeadMenu(null) }), _jsxs("span", { className: "tmx-hmenu", role: "menu", children: [_jsx("button", { type: "button", className: !f.value ? 'tmx-hopt-on' : '', onClick: () => { f.set(''); setHeadMenu(null); }, children: "All" }), f.options.map((o) => (_jsx("button", { type: "button", className: f.value === o.v ? 'tmx-hopt-on' : '', onClick: () => { f.set(o.v); setHeadMenu(null); }, children: o.label }, o.v)))] })] }))] }));
    };
    return (_jsxs("div", { className: `tmx ${selectedIds.length ? 'tbc-selmode' : ''}`, children: [_jsxs("div", { className: "tmx-toolbar", children: [_jsx("input", { className: "tmx-search", type: "search", placeholder: "Filter\u2026", value: q, onChange: (e) => setQ(e.target.value), "aria-label": "Filter tasks" }), _jsxs("span", { className: "tmx-count", children: [selectedIds.length ? `${selectedIds.length} selected / ` : '', rows.length, " task(s)"] }), _jsx("span", { className: "tmx-spacer" }), notice && _jsx("span", { className: "tmx-notice", children: notice }), canUndo && (_jsx("button", { type: "button", className: "tmx-export", onClick: undo, title: "Undo last change (Cmd+Z)", "aria-label": "Undo", children: _jsx(Undo2, { size: 14 }) })), _jsx("button", { type: "button", className: "tmx-export", onClick: copyForSlack, title: "Copy as Slack-ready text", "aria-label": "Copy for Slack", children: _jsx(MessageSquare, { size: 14 }) }), _jsx("button", { type: "button", className: "tmx-export", onClick: copyForEmail, title: "Copy a formatted table \u2014 paste into an email", "aria-label": "Copy for email", children: _jsx(Copy, { size: 14 }) }), _jsx("button", { type: "button", className: "tmx-export", onClick: downloadCsv, title: "Download CSV", "aria-label": "Download CSV", children: _jsx(Download, { size: 14 }) }), onExportSlides && (_jsx("button", { type: "button", className: "tmx-export", onClick: () => {
                            const target = exportRows();
                            onExportSlides(target.map((t) => t.id));
                            onLogEvents?.(exportEvents(target, 'exported_slides', {}));
                            clearSelection();
                        }, title: "Build a Slides deck from these rows", "aria-label": "Export to Slides", children: _jsx(Presentation, { size: 14 }) }))] }), _jsxs("div", { className: "tmx-scroll", children: [_jsxs("table", { className: "tmx-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "tmx-th tmx-th-sel", "aria-label": "Select" }), _jsx("th", { className: "tmx-th tmx-th-check", "aria-label": "Complete" }), header('Date', 'due'), header('Task', 'title'), header('Account', 'account'), header('Assignee', 'assignee'), header('Priority', 'priority'), header('List', 'list')] }) }), _jsx("tbody", { children: rows.map((t) => {
                                    const gone = !!struck[t.id] || t.status === 'completed';
                                    const isEd = (f) => editing?.id === t.id && editing.field === f;
                                    return (_jsxs("tr", { className: `tmx-row ${gone ? 'tmx-row-done' : ''} ${selected[t.id] ? 'tmx-row-selected' : ''}`, onClick: (e) => {
                                            if (e.shiftKey || e.metaKey) {
                                                e.preventDefault();
                                                setSelected((s2) => ({ ...s2, [t.id]: !s2[t.id] }));
                                            }
                                        }, children: [_jsx("td", { className: "tmx-td tmx-td-sel", children: _jsx("button", { type: "button", className: `tbc-sel ${selected[t.id] ? 'tbc-sel-on' : ''}`, role: "checkbox", "aria-checked": !!selected[t.id], "aria-label": selected[t.id] ? 'Deselect task' : 'Select task', title: selected[t.id] ? 'Deselect' : 'Select for bulk actions', onClick: (e) => {
                                                        e.stopPropagation();
                                                        setSelected((s2) => ({ ...s2, [t.id]: !s2[t.id] }));
                                                    } }) }), _jsx("td", { className: "tmx-td tmx-td-check", children: _jsx("button", { type: "button", className: `tmx-check ${gone ? 'tmx-check-done' : ''}`, "aria-label": gone ? 'Reopen task' : 'Complete task', title: gone ? 'Done — click to reopen' : 'Complete', onClick: () => (t.status === 'completed' ? onReopen?.(t.id) : check(t.id)), children: _jsx(Check, { size: 13, strokeWidth: 3, "aria-hidden": true }) }) }), _jsx("td", { className: "tmx-td tmx-td-date", children: isEd('due') ? (_jsx("input", { type: "date", className: "tmx-edit", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: commitEdit, onKeyDown: (e) => { if (e.key === 'Enter')
                                                        commitEdit(); if (e.key === 'Escape')
                                                        setEditing(null); } })) : (_jsx("button", { type: "button", className: "tmx-cell-btn", onClick: () => startEdit(t.id, 'due', isoOf(t.due_date)), children: fmtDate(t.due_date) || '—' })) }), _jsx("td", { className: "tmx-td tmx-td-task", children: isEd('title') ? (_jsx("input", { className: "tmx-edit tmx-edit-title", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: commitEdit, onKeyDown: (e) => { if (e.key === 'Enter')
                                                        commitEdit(); if (e.key === 'Escape')
                                                        setEditing(null); } })) : (_jsxs("button", { type: "button", className: "tmx-cell-btn tmx-title", onClick: () => startEdit(t.id, 'title', t.title), children: [t.today && _jsx(Sun, { size: 11, className: "tmx-today", "aria-label": "In today's plan" }), t.title, t.description && _jsx("span", { className: "tmx-detail", children: t.description })] })) }), _jsx("td", { className: "tmx-td", children: t.account_label || '' }), _jsx("td", { className: "tmx-td", children: isEd('assignee') ? (_jsxs(_Fragment, { children: [_jsx("input", { className: "tmx-edit", list: "tmx-people", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: commitEdit, onKeyDown: (e) => { if (e.key === 'Enter')
                                                                commitEdit(); if (e.key === 'Escape')
                                                                setEditing(null); } }), _jsx("datalist", { id: "tmx-people", children: (people || []).map((p) => (_jsx("option", { value: p.name }, `${p.name}:${p.email || ''}`))) })] })) : (_jsx("button", { type: "button", className: "tmx-cell-btn", onClick: () => startEdit(t.id, 'assignee', t.waiting_for_who || ''), children: t.waiting_for_who || '—' })) }), _jsx("td", { className: "tmx-td tmx-td-heat", children: onUpdate ? (_jsx("input", { type: "range", className: `tbc-heat tmx-heat tbc-band-${priorityBand(taskScore(t))}`, min: 0, max: 100, defaultValue: taskScore(t), "aria-label": "Priority", title: "Priority: neutral / yellow / red", onPointerUp: (e) => {
                                                        const v = snapScore(Number(e.target.value));
                                                        e.target.value = String(v);
                                                        if (v !== taskScore(t))
                                                            undoableUpdate(t.id, { priority_score: v });
                                                    }, onKeyUp: (e) => {
                                                        const v = snapScore(Number(e.target.value));
                                                        if (v !== taskScore(t))
                                                            undoableUpdate(t.id, { priority_score: v });
                                                    } })) : (t.priority || 'medium') }), _jsx("td", { className: "tmx-td", children: onUpdate ? (_jsx("select", { className: "tmx-inline-select", value: t.gtd_list, onChange: (e) => undoableUpdate(t.id, { gtd_list: e.target.value }), children: LISTS.map((l) => (_jsx("option", { value: l, children: l.replace('_', ' ') }, l))) })) : (t.gtd_list.replace('_', ' ')) })] }, t.id));
                                }) })] }), rows.length === 0 && _jsx("div", { className: "tmx-empty", children: "No tasks match these filters." })] }), _jsx(TaskBulkBar, { tasks: rows.filter((t) => selected[t.id]), people: people, moveOptions: LISTS.map((l) => ({ key: l, label: l.replace('_', ' ') })), moveLabel: "Move to list", onMove: onUpdate ? (id, key) => undoableUpdate(id, { gtd_list: key }) : undefined, onComplete: onComplete ? (id) => check(id) : undefined, onArchive: shelf.archive, onBulkArchive: shelf.archiveMany, onDelete: onDelete, onAssign: onAssign, onLogEvents: onLogEvents, onClear: clearSelection })] }));
}
export default TaskMatrix;
